import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { ArrowLeft, Save, Printer, Loader2, Plus, Trash2, Ship } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import SeaMtDocumentPrint from './SeaMtDocumentPrint';
import SearchableSelect from '../../components/SearchableSelect';
import { CONTAINER_TYPE_OPTIONS } from '../../constants/containerTypes';

const EMPTY_CONTAINER = {
  container_no: '',
  container_type: '',
  packages: '',
  gross_weight: '',
  net_weight: '',
  line_seal_no: '',
  custom_seal_no: '',
  measurement: '',
  description: '',
  sort_order: 1,
};

const EMPTY_FORM = {
  job_no: '',
  mode_of_transport: 'SEA',
  consignor_shipper: '',
  consignor_address: '',
  consignee: '',
  consignee_address: '',
  notify_party: '',
  notify_party_address: '',
  mtd_bl_number: '',
  shipment_reference_no: '',
  place_of_acceptance: '',
  port_of_loading: '',
  port_of_discharge: '',
  place_of_delivery: '',
  route_place_of_transport: '',
  vessel: '',
  voyage_no: '',
  modes_means_transport: 'SEA',
  marks_and_numbers: '',
  description_of_goods: '',
  fcl_lcl: 'FCL',
  measurement: '',
  select_type: '',
  freight_payable_at: 'DESTINATION',
  no_of_original_mtds: 0,
  agent_detail: '',
  shipped_on_board_date: '',
  place_of_issue: '',
  freight_amount: 'COLLECT',
  date_of_issue: '',
  container_layout: 'auto',
  containers: [{ ...EMPTY_CONTAINER }],
};

const SeaMtDocumentForm = () => {
  const { docketId, mtDocId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const isEdit = !!mtDocId;
  // Auto-activate print view when navigating to /<id>/print path
  const isPrintRoute = location.pathname.endsWith('/print');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [companyId, setCompanyId] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [companyDetails, setCompanyDetails] = useState(null);
  const [docketNo, setDocketNo] = useState('');
  const [showPrint, setShowPrint] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [savedId, setSavedId] = useState(mtDocId || null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const getAuth = useCallback(() => ({
    Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
    'Content-Type': 'application/json',
  }), []);

  // Auto-show print view if navigated to the /print route
  useEffect(() => {
    if (isPrintRoute && !loading) {
      setShowPrint(true);
    }
  }, [isPrintRoute, loading]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        // Always fetch docket info
        const docketRes = await fetch(`${backendUrl}/api/dockets/${docketId}`, { headers: getAuth() });
        if (docketRes.ok) {
          const d = await docketRes.json();
          setDocketNo(d.system_docket_no || docketId);
          const cid = d.company_id || '';
          setCompanyId(cid);
          // Fetch company name for print
          if (cid) {
            const compRes = await fetch(`${backendUrl}/api/companies/${cid}`, { headers: getAuth() });
            if (compRes.ok) {
              const comp = await compRes.json();
              setCompanyName(comp.name || '');
              setCompanyDetails(comp);
            }
          } else {
            const allCompRes = await fetch(`${backendUrl}/api/companies`, { headers: getAuth() });
            if (allCompRes.ok) {
              const allComps = await allCompRes.json();
              if (Array.isArray(allComps) && allComps.length > 0) {
                const comp = allComps.find(c => c.name?.includes('INTERNATIONAL') || c.logo_url) || allComps[0];
                setCompanyName(comp.name || '');
                setCompanyDetails(comp);
              }
            }
          }
        }

        if (isEdit) {
          // Load existing MT doc
          const docRes = await fetch(`${backendUrl}/api/sea-mt-documents/${mtDocId}`, { headers: getAuth() });
          if (!docRes.ok) throw new Error('Failed to load BL / MT Document');
          const doc = await docRes.json();
          setFormData({
            ...doc,
            shipped_on_board_date: doc.shipped_on_board_date || '',
            date_of_issue: doc.date_of_issue || '',
            containers: doc.containers && doc.containers.length > 0
              ? doc.containers
              : [{ ...EMPTY_CONTAINER }],
          });
          setSavedId(doc.id);
        } else {
          // Prefill from docket
          const prefillRes = await fetch(
            `${backendUrl}/api/sea-mt-documents/prefill-data?docket_id=${docketId}`,
            { headers: getAuth() }
          );
          if (prefillRes.ok) {
            const prefill = await prefillRes.json();
            setFormData(prev => ({ ...prev, ...prefill, containers: prefill.containers || [{ ...EMPTY_CONTAINER }] }));
          }
        }
      } catch (err) {
        toast({ title: 'Error', description: err.message, variant: 'destructive' });
      } finally {
        setLoading(false);
      }
    })();
  }, [docketId, mtDocId, isEdit, backendUrl, getAuth, toast]);

  const set = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const updateContainer = (idx, field, value) => {
    setFormData(prev => ({
      ...prev,
      containers: prev.containers.map((c, i) => i === idx ? { ...c, [field]: value } : c),
    }));
  };

  const addContainer = () =>
    setFormData(prev => ({
      ...prev,
      containers: [...prev.containers, { ...EMPTY_CONTAINER, sort_order: prev.containers.length + 1 }],
    }));

  const removeContainer = (idx) => {
    if (formData.containers.length <= 1) return;
    setFormData(prev => ({ ...prev, containers: prev.containers.filter((_, i) => i !== idx) }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      const payload = {
        ...formData,
        docket_id: docketId,
        company_id: companyId,
        no_of_original_mtds: parseInt(formData.no_of_original_mtds) || 0,
        shipped_on_board_date: formData.shipped_on_board_date || null,
        date_of_issue: formData.date_of_issue || null,
        containers: formData.containers.map((c, i) => ({ ...c, sort_order: i + 1 })),
      };

      const url = isEdit
        ? `${backendUrl}/api/sea-mt-documents/${savedId}`
        : `${backendUrl}/api/sea-mt-documents`;
      const method = isEdit ? 'PUT' : 'POST';

      const res = await fetch(url, { method, headers: getAuth(), body: JSON.stringify(payload) });
      if (!res.ok) {
        const errJson = await res.json().catch(() => ({}));
        throw new Error(errJson.detail || 'Save failed');
      }
      const saved = await res.json();
      setSavedId(saved.id);
      toast({ title: 'Saved', description: 'BL / MT Document saved successfully' });

      if (!isEdit) {
        // Redirect to edit mode so subsequent saves use PUT
        navigate(`/dockets/${docketId}/sea-mt-documents/${saved.id}/edit`, { replace: true });
      }
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (showPrint) {
    return (
      <div className="min-h-screen bg-gray-100/60 p-4 sm:p-6 print:p-0 print:bg-white">
        {/* Top Control Bar - aligned with document preview, zero collision with sidebar or navbar */}
        <div className="print:hidden max-w-[210mm] mx-auto mb-5 bg-white p-3 rounded-xl shadow-sm border border-gray-200 flex flex-wrap items-center justify-between gap-3">
          <Button variant="outline" size="sm" onClick={() => setShowPrint(false)} className="bg-white hover:bg-gray-50 border-gray-300">
            <ArrowLeft className="w-4 h-4 mr-2" />Back to Form
          </Button>

          {/* Quick Container Layout Switcher */}
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Container Layout:</span>
            <div className="inline-flex rounded-lg border border-gray-200 bg-gray-100 p-0.5">
              {[
                { id: 'auto', label: 'Auto (>3 / Filled)' },
                { id: 'table', label: 'Bottom Table Grid' },
                { id: 'standard', label: 'Standard Columns' },
              ].map(opt => (
                <button
                  key={opt.id}
                  onClick={() => setFormData(prev => ({ ...prev, container_layout: opt.id }))}
                  className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${
                    (formData.container_layout || 'auto') === opt.id
                      ? 'bg-teal-600 text-white shadow-xs font-semibold'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <Button size="sm" onClick={() => window.print()} className="bg-teal-600 hover:bg-teal-700 text-white shadow-sm font-medium">
            <Printer className="w-4 h-4 mr-2" />Print / Save PDF
          </Button>
        </div>

        {/* Document Print Area */}
        <div className="print:m-0 print:p-0">
          <SeaMtDocumentPrint formData={formData} docketNo={docketNo} companyName={companyName} company={companyDetails} />
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  const F = ({ label, field, inputType = 'text', readOnly = false, placeholder = '', required = false }) => (
    <div>
      <Label className="text-xs font-medium text-gray-500">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </Label>
      <Input
        type={inputType}
        value={formData[field] || ''}
        onChange={e => set(field, e.target.value)}
        readOnly={readOnly}
        placeholder={placeholder}
        className={`mt-1 h-9 text-sm ${readOnly ? 'bg-gray-50' : ''}`}
        data-testid={`sea-mt-${field}`}
      />
    </div>
  );

  const TA = ({ label, field, rows = 3, placeholder = '' }) => (
    <div>
      <Label className="text-xs font-medium text-gray-500">{label}</Label>
      <Textarea
        value={formData[field] || ''}
        onChange={e => set(field, e.target.value)}
        rows={rows}
        placeholder={placeholder}
        className="mt-1 text-sm"
        data-testid={`sea-mt-${field}`}
      />
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-5xl mx-auto space-y-5">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(`/dockets/${docketId}/sea-mt-documents`)}>
              <ArrowLeft className="w-4 h-4 mr-1" />Back to BL / MT Documents
            </Button>
            <div>
              <h1 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <Ship className="w-5 h-5 text-teal-600" />
                {isEdit ? 'Edit BL / MT Document' : 'New BL / MT Document'}
              </h1>
              <p className="text-xs text-gray-500">Docket: {docketNo}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {savedId && (
              <Button
                variant="outline"
                onClick={() => setShowPrint(true)}
                className="border-teal-600 text-teal-600 hover:bg-teal-50"
                data-testid="sea-mt-print-btn"
              >
                <Printer className="w-4 h-4 mr-2" />Print
              </Button>
            )}
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-teal-600 hover:bg-teal-700"
              data-testid="sea-mt-save-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>

        {/* Section 1 – Document Info */}
        <Card className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-teal-700 to-teal-600 px-5 py-3">
            <h2 className="text-sm font-semibold text-white">Document Info</h2>
          </div>
          <div className="p-5 space-y-4">

            {/* Row 1 */}
            <div className="grid grid-cols-2 gap-4">
              <F label="Job No." field="job_no" readOnly />
              <F label="Mode Of Transport" field="mode_of_transport" readOnly />
            </div>

            {/* Row 2 – Consignor */}
            <div className="grid grid-cols-2 gap-4">
              <F label="Consignor / Shipper" field="consignor_shipper" placeholder="Company name" />
              <TA label="Consignor Address" field="consignor_address" rows={3} placeholder="Full address" />
            </div>

            {/* Row 3 – Consignee */}
            <div className="grid grid-cols-2 gap-4">
              <F label="Consignee (or Order)" field="consignee" placeholder="Consignee name" />
              <TA label="Consignee Address" field="consignee_address" rows={3} placeholder="Full address" />
            </div>

            {/* Row 4 – Notify Party */}
            <div className="grid grid-cols-2 gap-4">
              <F label="Notify Party" field="notify_party" placeholder="Notify party name" />
              <TA label="Notify Party Address" field="notify_party_address" rows={3} placeholder="Full address" />
            </div>

            {/* Row 5 – BL / Refs */}
            <div className="grid grid-cols-3 gap-4">
              <F label="MTD / BL Number" field="mtd_bl_number" placeholder="e.g. 252631641" />
              <F label="Shipment Reference No." field="shipment_reference_no" placeholder="Reference number" />
              <F label="Route / Place of Transport" field="route_place_of_transport" />
            </div>

            {/* Row 6 – Ports */}
            <div className="grid grid-cols-4 gap-4">
              <F label="Place of Acceptance" field="place_of_acceptance" placeholder="e.g. CHICAGO, IL" />
              <F label="Port of Loading" field="port_of_loading" placeholder="e.g. NORFOLK, VA" />
              <F label="Port of Discharge" field="port_of_discharge" placeholder="e.g. MUNDRA, INDIA" />
              <F label="Place of Delivery" field="place_of_delivery" placeholder="e.g. MUNDRA, INDIA" />
            </div>

            {/* Row 7 – Vessel */}
            <div className="grid grid-cols-3 gap-4">
              <F label="Vessel" field="vessel" placeholder="e.g. BREMEN EXPRESS" />
              <F label="Voyage No." field="voyage_no" placeholder="e.g. 6201" />
              <F label="Modes / Means of Transport" field="modes_means_transport" placeholder="SEA" />
            </div>

          </div>
        </Card>

        {/* Section 2 – Container Info */}
        <Card className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-teal-700 to-teal-600 px-5 py-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-white">Container Info</h2>
            <span className="text-xs text-teal-200">{formData.containers.length} container(s)</span>
          </div>
          <div className="p-5 space-y-5">

            {/* Marks & Numbers / Description */}
            <div className="grid grid-cols-2 gap-4">
              <TA label="Marks and Numbers" field="marks_and_numbers" rows={4} placeholder="Marks and Number" />
              <TA label="Number/Kind of Packages & Description of Goods" field="description_of_goods" rows={4} placeholder="HS Code, description, AES, PO No, etc." />
            </div>

            {/* FCL / LCL */}
            <div className="flex items-center gap-6">
              <Label className="text-xs font-medium text-gray-500">Shipment Type</Label>
              {['FCL', 'LCL'].map(opt => (
                <label key={opt} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="fcl_lcl"
                    value={opt}
                    checked={formData.fcl_lcl === opt}
                    onChange={() => set('fcl_lcl', opt)}
                    className="accent-teal-600"
                    data-testid={`sea-mt-fcl-lcl-${opt.toLowerCase()}`}
                  />
                  <span className="text-sm text-gray-700">{opt}</span>
                </label>
              ))}
            </div>

                        {/* Container Print Layout Selector */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 bg-teal-50/70 border border-teal-200 rounded-lg">
              <div>
                <span className="text-xs font-semibold text-teal-900 block">Print Container Layout</span>
                <span className="text-[11px] text-teal-700">Display horizontal table at bottom if &gt;3 containers or description space is filled</span>
              </div>
              <div className="inline-flex rounded-lg border border-teal-200 bg-white p-0.5">
                {[
                  { id: 'auto', label: 'Auto (Recommended)' },
                  { id: 'table', label: 'Bottom Table Grid' },
                  { id: 'standard', label: 'Standard Columns' },
                ].map(opt => (
                  <button
                    type="button"
                    key={opt.id}
                    onClick={() => set('container_layout', opt.id)}
                    className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${
                      (formData.container_layout || 'auto') === opt.id
                        ? 'bg-teal-600 text-white shadow-xs'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Container rows */}
            <div className="space-y-3">
              {formData.containers.map((c, idx) => (
                <div key={idx} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Container {idx + 1}</span>
                    {formData.containers.length > 1 && (
                      <button
                        onClick={() => removeContainer(idx)}
                        className="text-red-400 hover:text-red-600 p-1 rounded"
                        data-testid={`sea-mt-remove-container-${idx}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-3 mb-3">
                    <div>
                      <Label className="text-xs font-medium text-gray-500">Container No.</Label>
                      <Input
                        value={c.container_no || ''}
                        onChange={e => updateContainer(idx, 'container_no', e.target.value)}
                        className="mt-1 h-9 text-sm"
                        placeholder="e.g. HLBU9588498"
                        data-testid={`sea-mt-container-no-${idx}`}
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-gray-500">Container Type</Label>
                      <SearchableSelect
                        data-testid={`sea-mt-container-type-${idx}`}
                        value={c.container_type || ''}
                        onValueChange={val => updateContainer(idx, 'container_type', val)}
                        placeholder="Select type..."
                        searchPlaceholder="Search container type..."
                        options={CONTAINER_TYPE_OPTIONS}
                        className="mt-1 h-9 text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-gray-500">Packages</Label>
                      <Input
                        value={c.packages || ''}
                        onChange={e => updateContainer(idx, 'packages', e.target.value)}
                        className="mt-1 h-9 text-sm"
                        placeholder="e.g. 131"
                        data-testid={`sea-mt-packages-${idx}`}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-5 gap-3">
                    <div>
                      <Label className="text-xs font-medium text-gray-500">Gross Weight</Label>
                      <Input
                        value={c.gross_weight || ''}
                        onChange={e => updateContainer(idx, 'gross_weight', e.target.value)}
                        className="mt-1 h-9 text-sm"
                        placeholder="e.g. 7530.800"
                        data-testid={`sea-mt-gross-weight-${idx}`}
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-gray-500">Net Weight</Label>
                      <Input
                        value={c.net_weight || ''}
                        onChange={e => updateContainer(idx, 'net_weight', e.target.value)}
                        className="mt-1 h-9 text-sm"
                        placeholder="e.g. 6511.800"
                        data-testid={`sea-mt-net-weight-${idx}`}
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-gray-500">CBM</Label>
                      <Input
                        value={c.measurement || ''}
                        onChange={e => updateContainer(idx, 'measurement', e.target.value)}
                        className="mt-1 h-9 text-sm"
                        placeholder="e.g. 25.00"
                        data-testid={`sea-mt-cbm-${idx}`}
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-gray-500">Line Seal No.</Label>
                      <Input
                        value={c.line_seal_no || ''}
                        onChange={e => updateContainer(idx, 'line_seal_no', e.target.value)}
                        className="mt-1 h-9 text-sm"
                        placeholder="e.g. 2033CT"
                        data-testid={`sea-mt-line-seal-${idx}`}
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-gray-500">Custom Seal No.</Label>
                      <Input
                        value={c.custom_seal_no || ''}
                        onChange={e => updateContainer(idx, 'custom_seal_no', e.target.value)}
                        className="mt-1 h-9 text-sm"
                        data-testid={`sea-mt-custom-seal-${idx}`}
                      />
                    </div>
                  </div>
                  {/* Per-container description */}
                  <div className="mt-3">
                    <Label className="text-xs font-medium text-gray-500">Container Description (No. of Pkgs, Kind, Goods Desc, AES, PO, HS Code)</Label>
                    <Textarea
                      value={c.description || ''}
                      onChange={e => updateContainer(idx, 'description', e.target.value)}
                      rows={4}
                      placeholder={`e.g.\n131 DRUMS\nLOADED ON 28 PALLETS\nPROPRANOLOL HCL SINGLE MILLED/DRM 50KG\nAES: X20260126484859\nPO NO: 6506092\nHS CODE: 29420027`}
                      className="mt-1 text-sm"
                      data-testid={`sea-mt-container-description-${idx}`}
                    />
                  </div>
                </div>
              ))}

              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addContainer}
                className="border-teal-500 text-teal-600 hover:bg-teal-50 text-xs"
                data-testid="sea-mt-add-container-btn"
              >
                <Plus className="w-3.5 h-3.5 mr-1" />Add Container
              </Button>
            </div>
          </div>
        </Card>

        {/* Section 3 – Additional Details */}
        <Card className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-teal-700 to-teal-600 px-5 py-3">
            <h2 className="text-sm font-semibold text-white">Additional Details</h2>
          </div>
          <div className="p-5 space-y-4">

            {/* Row 1 */}
            <div className="grid grid-cols-4 gap-4">
              <F label="Measurement" field="measurement" placeholder="e.g. 18.5 CBM" />
              <div>
                <Label className="text-xs font-medium text-gray-500">Select</Label>
                <Select
                  value={formData.select_type || ''}
                  onValueChange={val => set('select_type', val)}
                >
                  <SelectTrigger className="mt-1 h-9 text-sm" data-testid="sea-mt-select-type">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Express Release">Express Release</SelectItem>
                    <SelectItem value="Original BL">Original BL</SelectItem>
                    <SelectItem value="Sea Waybill">Sea Waybill</SelectItem>
                    <SelectItem value="Telex Release">Telex Release</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <F label="Freight Payable At" field="freight_payable_at" placeholder="e.g. DESTINATION" />
              <div>
                <Label className="text-xs font-medium text-gray-500">No. of Original MTD(s)</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.no_of_original_mtds ?? 0}
                  onChange={e => set('no_of_original_mtds', e.target.value)}
                  className="mt-1 h-9 text-sm"
                  data-testid="sea-mt-no-of-original-mtds"
                />
              </div>
            </div>

            {/* Agent Detail */}
            <TA label="Agent Detail" field="agent_detail" rows={3} placeholder="Agent name, address, contact..." />

            {/* Row 3 – Dates */}
            <div className="grid grid-cols-4 gap-4">
              <F label="Shipped On Board Date" field="shipped_on_board_date" inputType="date" />
              <F label="Place of Issue" field="place_of_issue" placeholder="e.g. ATLANTA, GA" />
              <div>
                <Label className="text-xs font-medium text-gray-500">Freight Amount</Label>
                <Select
                  value={formData.freight_amount || ''}
                  onValueChange={val => set('freight_amount', val)}
                >
                  <SelectTrigger className="mt-1 h-9 text-sm" data-testid="sea-mt-freight-amount">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="COLLECT">COLLECT</SelectItem>
                    <SelectItem value="PREPAID">PREPAID</SelectItem>
                    <SelectItem value="AS ARRANGED">AS ARRANGED</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <F label="Date of Issue" field="date_of_issue" inputType="date" />
            </div>

          </div>
        </Card>

        {/* Bottom Save */}
        <div className="flex justify-end gap-3 pb-4">
          <Button
            variant="outline"
            onClick={() => navigate(`/dockets/${docketId}/sea-mt-documents`)}
            data-testid="sea-mt-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-teal-600 hover:bg-teal-700"
            data-testid="sea-mt-bottom-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            {saving ? 'Saving...' : 'Save MT Document'}
          </Button>
        </div>

      </div>
    </div>
  );
};

export default SeaMtDocumentForm;
